import React, { useState, useRef, useEffect, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { Body, Ellipsis } from 'components/styled/typography'
import { LabelStyle, SmallStyle } from 'components/styled/typography/styles'
import { Box } from 'components/styled/containers/box'
import { Icon } from 'components/styled/icon'

export interface FieldProps {
  title: string
  subtitle?: string
  content: string | React.ReactNode
  ellipsis?: boolean
  onClickContent?: () => void
  expandFromIcon?: boolean
}

const StyledField = styled.div`
  margin-bottom: ${({ theme }) => theme.space.s20};
`

const FieldLabel = styled.span<{ $inRow?: boolean }>`
  ${LabelStyle};
  margin-bottom: ${({ theme, $inRow }) => ($inRow ? 0 : theme.space.s20)};
`

const FieldTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.space.s4};
`

const ExpandIconButton = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.color.grey90};
  cursor: pointer;
  display: inline-flex;
  line-height: 1;
  padding: ${({ theme }) => theme.space.s4};

  &:hover {
    color: ${({ theme }) => theme.color.grey70};
  }
`

const FieldSubLabel = styled.span`
  ${SmallStyle};
  font-style: italic;
  margin-left: ${({ theme }) => theme.space.s4};
  margin-bottom: ${({ theme }) => theme.space.s8};
`

const EllipsisStyle = css`
  ${Ellipsis};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const FieldBodyWrapper = styled.div<{
  $ellipsis: boolean
  $clickable: boolean
}>`
  ${({ $ellipsis }) => $ellipsis && EllipsisStyle}
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
`

export const Field: React.FC<FieldProps> = ({
  title,
  subtitle,
  content,
  ellipsis = true,
  expandFromIcon = false,
}) => {
  const isString = typeof content === 'string'
  const [expanded, setExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  const checkClamped = useCallback(() => {
    const el = bodyRef.current
    if (!el) return
    setIsClamped(el.scrollHeight > el.clientHeight)
  }, [])

  useEffect(() => {
    if (!ellipsis || !isString) return
    checkClamped()
  }, [content, ellipsis, isString, checkClamped])

  const canToggle = isClamped || expanded
  const showExpandIcon = expandFromIcon && canToggle

  const handleClick = () => {
    if (canToggle) {
      setExpanded((prev) => !prev)
    }
  }

  return (
    <StyledField>
      <Box mb="s8">
        {expandFromIcon ? (
          <FieldTitleRow>
            <FieldLabel $inRow>{title}</FieldLabel>
            {showExpandIcon && (
              <ExpandIconButton
                type="button"
                aria-expanded={expanded}
                onClick={handleClick}
              >
                <Icon type="expand" size="s16" />
              </ExpandIconButton>
            )}
          </FieldTitleRow>
        ) : (
          <FieldLabel>{title}</FieldLabel>
        )}
        {subtitle && <FieldSubLabel>{subtitle}</FieldSubLabel>}
      </Box>
      {isString ? (
        <FieldBodyWrapper
          ref={bodyRef}
          $ellipsis={ellipsis && !expanded}
          $clickable={canToggle}
          onClick={handleClick}
        >
          <Body whiteSpace="pre-wrap">{content}</Body>
        </FieldBodyWrapper>
      ) : (
        content
      )}
    </StyledField>
  )
}
